import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { VISUALIZATION_SERVICES, IMAGES } from "@/data/content";

const QUALITY_STANDARD = [
  "Physically based lighting and material response",
  "Global illumination and ray-traced reflections where supported",
  "Realistic shadows, ambient occlusion and depth",
  "Accurate material roughness, scale and texture mapping",
  "Detailed wood grain, veneer and pore representation",
  "Realistic stone, metal, glass, fabric and painted finishes",
  "Natural daylight and warm architectural lighting simulation",
  "Professional camera composition and exposure",
  "High-resolution output and presentation-ready post-production",
  "Geometry preservation when working from an approved model",
];

const RENDER_PROCESS = [
  "Project review — drawings, models, materials, references and required outputs are assessed.",
  "Model development — architecture, cabinetry, millwork, furniture and fixtures are created or refined.",
  "Material development — wood, stone, metal, glass, paint, fabric and flooring are accurately applied.",
  "Lighting development — natural and artificial lighting are created for the intended atmosphere.",
  "Camera composition — views are selected to communicate scale, focal points and spatial flow.",
  "Preview review — geometry, materials, composition and lighting are reviewed before final output.",
  "Final rendering — approved scenes are rendered at the required size and quality.",
  "Post-production — exposure, colour balance, reflections, atmosphere and presentation are refined.",
];

export default function Visualization() {
  return (
    <div data-testid="page-visualization">
      <PageHero
        overline="— Service / 01"
        title="High-end architectural"
        accent="visualization."
        body="Photorealistic imagery that communicates design, atmosphere, material quality, lighting and spatial experience before construction or manufacturing begins."
        image={IMAGES.renderArch}
        testid="viz-hero"
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-5">
          <div className="overline mb-3">Approach</div>
          <h2 className="font-serif text-4xl leading-tight">Every image is developed with intent.</h2>
        </Reveal>
        <Reveal className="md:col-span-6 md:col-start-7 text-[#A3A3A3] text-lg leading-relaxed" delay={120}>
          <p>
            Architectural accuracy, cabinet proportions, millwork detailing, material scale, wood
            grain direction, surface texture, natural and artificial lighting, reflection quality,
            shadow behaviour, hardware placement, appliance integration, decorative styling, camera
            composition and colour balance — all considered deliberately.
          </p>
          <p className="mt-6 text-[#EAEAEA]">
            When working from an approved model, cabinet geometry, dimensions, configuration,
            appliance and plumbing locations and architectural conditions are preserved.
          </p>
        </Reveal>
      </section>

      <section className="bg-[#0E0E0E] border-y border-white/5 py-24" data-testid="viz-services">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline mb-3">— Visualization services</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-12 max-w-3xl">From a single hero rendering to an immersive walkthrough.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {VISUALIZATION_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 40}>
                <div className="dl-card h-full p-7">
                  <div className="num text-3xl text-[#B58B5D] mb-3">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-serif text-xl text-[#EAEAEA] mb-3">{s.title}</h3>
                  <p className="text-sm text-[#A3A3A3] leading-relaxed mb-4">{s.body}</p>
                  <p className="text-xs font-mono uppercase tracking-[0.15em] text-[#B58B5D] leading-relaxed">{s.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-12" data-testid="viz-quality">
        <Reveal className="md:col-span-5">
          <div className="overline mb-3">Quality standard</div>
          <h2 className="font-serif text-4xl leading-tight">Ten technical commitments behind every Designlayer image.</h2>
        </Reveal>
        <Reveal className="md:col-span-7" delay={120}>
          <ul className="grid sm:grid-cols-2 gap-x-8">
            {QUALITY_STANDARD.map((q, i) => (
              <li key={q} className="py-3 border-b border-white/5 flex gap-4">
                <span className="num text-[#B58B5D]">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[15px] text-[#EAEAEA]">{q}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      <section className="bg-[#0E0E0E] border-y border-white/5 py-24" data-testid="viz-process">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline mb-3">— Rendering process</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-12">Eight stages, every project.</h2>
          </Reveal>
          <ol className="grid md:grid-cols-2 gap-x-12">
            {RENDER_PROCESS.map((p, i) => (
              <Reveal key={p} delay={i * 40}>
                <li className="flex gap-6 py-6 border-b border-white/5">
                  <span className="num text-3xl text-[#B58B5D] shrink-0 w-12">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[15px] text-[#EAEAEA] leading-relaxed">{p}</span>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl mb-6">Request a <em className="text-[#B58B5D] not-italic">visualization quote.</em></h2>
          <p className="text-[#A3A3A3] max-w-xl mx-auto mb-8">Send your plans, model files or reference images. We will respond with a scope, timeline and recommended output package.</p>
          <Link to="/contact" className="dl-btn-primary">Start a rendering project</Link>
        </Reveal>
      </section>
    </div>
  );
}
